import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

import { cn } from '@/lib/ui/cn'

interface DialogProps {
  onClose: () => void
  /** id of the element that names the dialog (its heading). */
  labelledBy: string
  describedBy?: string
  /** False while an action is in flight: Escape and the backdrop do nothing. */
  dismissible?: boolean
  size?: 'sm' | 'md'
  className?: string
  children: ReactNode
}

const WIDTH = { sm: 'w-[min(92vw,var(--container-md))]', md: 'w-[min(94vw,var(--container-lg))]' } as const

/**
 * A modal native `<dialog>`, mounted open: the browser puts it in the top
 * layer, makes the page behind it inert and offers Escape; focus returns to
 * whatever opened it. Where `showModal` is missing (jsdom) it opens in place.
 */
export function Dialog({ onClose, labelledBy, describedBy, dismissible = true, size = 'sm', className, children }: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal()
      else dialog.setAttribute('open', '')
    }
    return () => {
      if (dialog.open) {
        if (typeof dialog.close === 'function') dialog.close()
        else dialog.removeAttribute('open')
      }
      if (opener?.isConnected) opener.focus({ preventScroll: true })
    }
  }, [])

  return (
    <dialog
      ref={dialogRef}
      aria-modal="true"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      onCancel={event => {
        event.preventDefault()
        if (dismissible) onClose()
      }}
      onClick={event => {
        if (!dismissible || event.target !== event.currentTarget) return
        const rect = event.currentTarget.getBoundingClientRect()
        const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom
        if (outside) onClose()
      }}
      className={cn(
        'bl-dialog m-auto max-h-[calc(100dvh-var(--spacing)*8)] overflow-y-auto overscroll-contain rounded-[var(--radius-xl)] border border-[color:var(--bl-line-strong)] bg-[color:var(--bl-raised)] p-0 text-[color:var(--bl-ink)] shadow-[var(--bl-shadow-float)]',
        WIDTH[size],
        className,
      )}
    >
      {children}
    </dialog>
  )
}
